import React from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getProjectGroups, updateProjectGroups } from '../../../../../helpers/project-helper';

const extractGroupIds = (groups = []) => {
    if (!Array.isArray(groups)) {
        return [];
    }

    return groups
        .map((group) => (typeof group === 'object' ? group?._id : group))
        .filter(Boolean);
};

export const ProjectGroupsTabContent = ({ projectId }) => {
    const { token } = useSelector((state) => state.auth);

    const [availableGroups, setAvailableGroups] = React.useState([]);
    const [projectGroups, setProjectGroups] = React.useState([]);
    const [selectedGroupIds, setSelectedGroupIds] = React.useState([]);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(true);
    const [isSaving, setIsSaving] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        const loadGroups = async () => {
            if (!token || !projectId) {
                setError('No se pudo obtener la configuración de grupos del proyecto.');
                setAvailableGroups([]);
                setProjectGroups([]);
                setSelectedGroupIds([]);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError('');

            const resp = await getProjectGroups(token, projectId);

            if (!resp?.success) {
                setError(resp?.message || 'No se pudo obtener la configuración de grupos del proyecto.');
                setAvailableGroups([]);
                setProjectGroups([]);
                setSelectedGroupIds([]);
                setIsLoading(false);
                return;
            }

            const nextAvailableGroups = Array.isArray(resp?.data?.availableGroups) ? resp.data.availableGroups : [];
            const nextProjectGroups = Array.isArray(resp?.data?.groups) ? resp.data.groups : [];

            setAvailableGroups(nextAvailableGroups);
            setProjectGroups(nextProjectGroups);
            setSelectedGroupIds(extractGroupIds(nextProjectGroups));
            setIsLoading(false);
        };

        loadGroups();
    }, [token, projectId]);

    const availableGroupsById = React.useMemo(() => {
        return availableGroups.reduce((accumulator, group) => {
            if (!group?._id) {
                return accumulator;
            }

            return {
                ...accumulator,
                [group._id]: group,
            };
        }, {});
    }, [availableGroups]);

    const projectGroupsById = React.useMemo(() => {
        return projectGroups.reduce((accumulator, group) => {
            if (!group?._id) {
                return accumulator;
            }

            return {
                ...accumulator,
                [group._id]: group,
            };
        }, {});
    }, [projectGroups]);

    const selectedGroups = React.useMemo(() => {
        return selectedGroupIds.map((groupId) => {
            const existingGroup = availableGroupsById[groupId] || projectGroupsById[groupId];

            if (existingGroup) {
                return existingGroup;
            }

            return {
                _id: groupId,
                code: '-',
                name: 'Grupo no disponible',
                status: '-',
            };
        });
    }, [selectedGroupIds, availableGroupsById, projectGroupsById]);

    const filteredGroups = React.useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        const selectedSet = new Set(selectedGroupIds);

        if (!normalizedSearch) {
            return [];
        }

        return availableGroups
            .filter((group) => {
                const groupId = group?._id;

                if (!groupId || selectedSet.has(groupId)) {
                    return false;
                }

                const normalizedName = String(group?.name || '').toLowerCase();
                const normalizedCode = String(group?.code || '').toLowerCase();
                const normalizedStatus = String(group?.status || '').toLowerCase();

                return normalizedName.includes(normalizedSearch)
                    || normalizedCode.includes(normalizedSearch)
                    || normalizedStatus.includes(normalizedSearch);
            })
            .slice(0, 20);
    }, [availableGroups, selectedGroupIds, searchTerm]);

    const handleAddGroup = (groupId) => {
        setSelectedGroupIds((prev) => {
            if (prev.includes(groupId)) {
                return prev;
            }

            return [...prev, groupId];
        });
        setSearchTerm('');
    };

    const handleRemoveGroup = (groupId) => {
        setSelectedGroupIds((prev) => prev.filter((item) => item !== groupId));
    };

    const handleSaveGroups = async () => {
        if (!token || !projectId || isSaving) {
            return;
        }

        setIsSaving(true);
        setError('');
        const savingToastId = toast.loading('Guardando grupos del proyecto...');

        const resp = await updateProjectGroups(token, projectId, {
            groups: selectedGroupIds,
        });

        if (!resp?.success) {
            const message = resp?.message || 'No se pudo actualizar la configuración de grupos del proyecto.';
            setError(message);
            toast.update(savingToastId, {
                render: message,
                type: 'error',
                isLoading: false,
                autoClose: 3000,
                closeOnClick: true,
            });
            setIsSaving(false);
            return;
        }

        const nextProjectGroups = Array.isArray(resp?.data?.groups) ? resp.data.groups : [];

        setProjectGroups(nextProjectGroups);
        setSelectedGroupIds(extractGroupIds(nextProjectGroups));
        toast.update(savingToastId, {
            render: resp?.message || 'Grupos actualizados correctamente.',
            type: 'success',
            isLoading: false,
            autoClose: 2500,
            closeOnClick: true,
        });
        setIsSaving(false);
    };

    return (
        <div>
            <div className="mb-4 d-flex align-items-center justify-content-between">
                <h5 className="fw-bold mb-0">Grupos asignables para tickets del proyecto</h5>
                <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleSaveGroups}
                    disabled={isSaving || isLoading}
                >
                    {isSaving ? 'Guardando...' : 'Guardar grupos'}
                </button>
            </div>

            {isLoading && <div className="py-3 text-muted">Cargando grupos...</div>}

            {!isLoading && error && (
                <div className="alert alert-danger py-2" role="alert">{error}</div>
            )}

            {!isLoading && !error && availableGroups.length === 0 && (
                <div className="py-3 text-muted">No hay grupos activos disponibles en el tenant.</div>
            )}

            {!isLoading && !error && availableGroups.length > 0 && (
                <>
                    <div className="mb-4">
                        <label htmlFor="project-group-search" className="form-label">Buscar grupo</label>
                        <input
                            id="project-group-search"
                            type="text"
                            className="form-control"
                            placeholder="Escribe código, nombre o estatus"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                        />

                        {searchTerm.trim().length > 0 && (
                            <div className="border border-gray-3 rounded-2 mt-2">
                                {filteredGroups.length === 0 && (
                                    <div className="p-2 text-muted">Sin resultados para esta búsqueda.</div>
                                )}

                                {filteredGroups.length > 0 && (
                                    <div className="list-group list-group-flush">
                                        {filteredGroups.map((group) => (
                                            <button
                                                key={group._id}
                                                type="button"
                                                className="list-group-item list-group-item-action d-flex align-items-center justify-content-between"
                                                onClick={() => handleAddGroup(group._id)}
                                            >
                                                <span>{group?.name || '-'} ({group?.code || '-'})</span>
                                                <span className="badge bg-soft-primary text-primary">Agregar</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {selectedGroups.length === 0 && (
                        <div className="py-2 text-muted">No hay grupos seleccionados para este proyecto.</div>
                    )}

                    {selectedGroups.length > 0 && (
                        <div className="table-responsive">
                            <table className="table table-bordered mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>Código</th>
                                        <th>Nombre</th>
                                        <th>Estatus</th>
                                        <th style={{ width: '90px' }}>Acción</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedGroups.map((group) => {
                                        const groupId = group?._id;
                                        return (
                                            <tr key={groupId}>
                                                <td>{group?.code || '-'}</td>
                                                <td>{group?.name || '-'}</td>
                                                <td>{group?.status || '-'}</td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-sm"
                                                        onClick={() => handleRemoveGroup(groupId)}
                                                    >
                                                        Quitar
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
